import { isValid, parse } from "date-fns";
import * as yup from "yup";

import { ScheduleType } from "@/api/schedule/models";
import { PhysicalCharacteristicEnum } from "@/enums/characteristic";
import { ClothingSizeEnum, ShoeSizeEnum } from "@/enums/measurements";
import { DisabilityTypeEnum } from "@/enums/talent";
import { PhotogenyRejectionReasonEnum } from "@/enums/photogeny";

import { verifyIfTalentIsAdult } from "./features/talent/validations";
import { getHtmlContent } from "./helpers";
import { isNumericRangeValid } from "./number";

export type FormValidations = keyof typeof validations;

const requiredMessage = "Campo obrigatório.";

const CURRENT_YEAR = new Date().getFullYear();

function createFullDateValidation() {
  return yup
    .string()
    .notRequired()
    .test("is-complete-date", "Digite uma data válida (DD/MM/AAAA).", function (value) {
      if (!value || value.trim() === "") {
        return true;
      }
      if (value.length !== 10) {
        return false;
      }
      const dateRegex = /^(\d{2})\/(\d{2})\/(\d{4})$/;
      const match = value.match(dateRegex);
      if (!match) {
        return false;
      }
      const parsedDate = parse(value, "dd/MM/yyyy", new Date());
      return isValid(parsedDate);
    });
}

function createOptionalNumericRangeValidation(min: number, max: number, label: string) {
  return yup
    .string()
    .notRequired()
    .test("numeric-only", `${label} deve conter apenas números.`, (value) => {
      if (value == null || value.trim() === "") {
        return true;
      }

      return /^\d+$/.test(value);
    })
    .test("numeric-range", `${label} deve estar entre ${min} e ${max}.`, (value) => {
      if (value == null || value.trim() === "") {
        return true;
      }

      if (!/^\d+$/.test(value)) {
        return false;
      }

      const numeric = Number(value);
      return numeric >= min && numeric <= max;
    });
}

function createOptionalAgeRangeValidation() {
  return yup
    .string()
    .notRequired()
    .test("age-format", "Informe idade no formato anos,meses.", (value) => {
      if (value == null || value.trim() === "") {
        return true;
      }

      return /^\d{1,3}(?:,\d{1,2})?$/.test(value);
    })
    .test("age-range", "Idade deve estar entre 0 e 110 anos.", (value) => {
      if (value == null || value.trim() === "") {
        return true;
      }

      const [years] = value.split(",").map(Number);
      return years <= 110;
    })
    .test("age-months", "Meses devem estar entre 0 e 11. Use 2,0 em vez de 1,12.", (value) => {
      if (value == null || value.trim() === "" || !/^\d{1,3}(?:,\d{1,2})?$/.test(value)) {
        return true;
      }

      const [, months] = value.split(",").map(Number);
      return months === undefined || months <= 11;
    });
}

function isAgeRangeValid(value?: { from?: string | null; to?: string | null } | null): boolean {
  const from = value?.from?.trim();
  const to = value?.to?.trim();

  if (!from || !to || !/^\d{1,3}(?:,\d{1,2})?$/.test(from) || !/^\d{1,3}(?:,\d{1,2})?$/.test(to)) {
    return true;
  }

  const toMonths = (age: string) => {
    const [years, months = "0"] = age.split(",");
    return Number(years) * 12 + Number(months);
  };

  return toMonths(from) <= toMonths(to);
}

function createResponsibleValidation(isAdult: boolean) {
  return yup.object().shape({
    fullName: isAdult ? yup.string().notRequired() : yup.string().required("Nome é obrigatório."),
    kinship: isAdult ? yup.string().notRequired() : yup.string().required("Parentesco é obrigatório."),
    phone: isAdult
      ? yup.string().notRequired()
      : yup
          .string()
          .required("Telefone é obrigatório.")
          .transform((value) => value.replace(/\D/g, ""))
          .min(10, "O número de telefone deve ter pelo menos 10 dígitos.")
          .max(11, "O número de telefone deve ter no máximo 11 dígitos.")
          .matches(/^\d+$/, "O número de telefone deve conter apenas números."),
    secondaryPhone: yup.string().notRequired(),
    email: isAdult
      ? yup.string().notRequired()
      : yup.string().email("E-mail inválido").required("E-mail é obrigatório."),
    birthday: yup
      .date()
      .transform((value, originalValue) => (originalValue === "" ? null : value))
      .notRequired(),
    rg: isAdult
      ? yup.string().notRequired()
      : yup
          .string()
          .notRequired()
          .transform((value) => (value ? value.replace(/\D/g, "") : ""))
          .test("rg-format", "RG inválido.", (value) => {
            if (!value || value.trim() === "") {
              return true;
            }
            return /^\d+$/.test(value) && value.length >= 9;
          }),
    cpf: isAdult
      ? yup.string().notRequired()
      : yup
          .string()
          .required("CPF é obrigatório.")
          .transform((value) => value.replace(/\D/g, ""))
          .matches(/^\d+$/, "CPF inválido.")
          .min(11, "CPF deve ter 11 dígitos."),
    sameAddressAsTalent: yup.boolean().notRequired(),
    address: yup.object().shape({
      zipCode: yup
        .string()
        .notRequired()
        .transform((value) => value.replace(/\D/g, ""))
        .matches(/^\d{8}$/, { message: "CEP inválido.", excludeEmptyString: true }),
      street: yup.string().notRequired(),
      number: yup.string().notRequired(),
      complement: yup.string().notRequired(),
      neighborhood: yup.string().notRequired(),
      city: yup.string().notRequired(),
      state: yup.string().notRequired(),
    }),
  });
}

const validateHeightRange = (value: string | undefined): boolean => {
  if (!value || value.trim() === "") {
    return true; // filtro vazio
  }
  const num = Number(value);
  return !Number.isNaN(num) && num >= 40 && num <= 250;
};

export const validations = {
  none: null,
  photogenyPhotographer: yup.string().trim().required(requiredMessage),
  photogenyEvaluationDate: yup.date().nullable().required(requiredMessage),
  photogenyResult: yup.boolean().nullable().required("Selecione o resultado."),
  photogenyRejectionReason: yup.string().when("isApproved", {
    is: false,
    then: (schema) =>
      schema
        .oneOf(Object.values(PhotogenyRejectionReasonEnum), "Selecione um motivo de reprovação válido.")
        .required("Selecione o motivo da reprovação."),
    otherwise: (schema) => schema.nullable().notRequired(),
  }),
  photogenyObservation: yup.string().nullable().max(500, "Máximo de 500 caracteres."),
  artisticProductionChanges: yup
    .array()
    .of(
      yup.object().shape({
        responsibleUserId: yup.string().required("Colaborador responsável é obrigatório."),
        clothing: yup.string().required("Vestuário é obrigatório.").max(250, "Máximo de 250 caracteres."),
        observation: yup.string().max(250, "Máximo de 250 caracteres."),
      }),
    )
    .required(),
  name: yup.string().required("Nome é obrigatório.").min(3, "Digite um nome válido."),
  documentName: yup
    .string()
    .required("Nome do documento é obrigatório.")
    .min(2, "Digite pelo menos 2 caracteres.")
    .max(120, "Digite no máximo 120 caracteres."),
  birthday: yup
    .date()
    .transform((value, originalValue) => (originalValue === "" ? null : value))
    .required("Data de nascimento é obrigatória."),
  rg: yup
    .string()
    .required("RG é obrigatório.")
    .transform((value) => value.replace(/\D/g, ""))
    .matches(/^\d+$/, "RG inválido.")
    .min(9, "RG deve ter no minimo 9 dígitos."),
  cpf: yup
    .string()
    .required("CPF é obrigatório.")
    .transform((value) => value.replace(/\D/g, ""))
    .matches(/^\d+$/, "CPF inválido.")
    .min(11, "CPF deve ter 11 dígitos."),
  cpfUnderageOptional: yup.string().when("birthday", {
    is: (birthday: Date) => verifyIfTalentIsAdult(birthday),
    then: (schema) =>
      schema
        .required("CPF é obrigatório.")
        .transform((value) => value.replace(/\D/g, ""))
        .matches(/^\d+$/, "CPF inválido.")
        .min(11, "CPF deve ter 11 dígitos."),
    otherwise: (schema) =>
      schema
        .transform((value) => (value ? value.replace(/\D/g, "") : null))
        .notRequired()
        .test("cpf-optional", "CPF deve ter 11 dígitos.", (value) => {
          if (value == null || value === "") {
            return true; // allow empty string
          }
          return /^\d+$/.test(value) && value.length >= 11;
        }),
  }),
  textMax80: yup
    .string()
    .required("Campo obrigatório")
    .min(3, "Mínimo de 3 caracteres")
    .max(80, "Máximo de 80 caracteres"),
  textMax80Optional: yup.string().max(80, "Máximo de 80 caracteres"),
  textMax100: yup
    .string()
    .required("Campo obrigatório")
    .min(3, "Mínimo de 3 caracteres")
    .max(100, "Máximo de 100 caracteres"),
  textMax100Optional: yup
    .string()
    .transform((value) => (value === "" ? undefined : value))
    .notRequired()
    .min(3, "Mínimo de 3 caracteres")
    .max(100, "Máximo de 100 caracteres"),
  textMax1000: yup.string().max(1000, "Máximo de 1000 caracteres"),
  textMax5000: yup.string().max(5000, "Máximo de 5000 caracteres"),
  hasIntermediary: yup.boolean().required("Campo obrigatório"),
  intermediaryName: yup.string().when("hasIntermediary", {
    is: true,
    then: (schema) =>
      schema.required(requiredMessage).min(3, "Mínimo de 3 caracteres").max(80, "Máximo de 80 caracteres"),
    otherwise: (schema) => schema.notRequired(),
  }),
  intermediaryEmail: yup.string().when("hasIntermediary", {
    is: true,
    then: (schema) => schema.required(requiredMessage).email("E-mail inválido."),
    otherwise: (schema) => schema.notRequired(),
  }),
  intermediaryPhone: yup.string().when("hasIntermediary", {
    is: true,
    then: (schema) => schema.required(requiredMessage),
    otherwise: (schema) => schema.notRequired(),
  }),
  clientEmail: yup.string().when("hasIntermediary", {
    is: false,
    then: (schema) => schema.email("E-mail inválido."),
    otherwise: (schema) => schema.notRequired(),
  }),
  clientPhone: yup.string().when("hasIntermediary", {
    is: false,
    then: (schema) =>
      schema
        .transform((value: string) => (value ? value.replace(/\D/g, "") : ""))
        .notRequired()
        .test("client-phone-length", "O número de telefone deve ter entre 10 e 11 dígitos.", (value) => {
          if (value == null || value.trim() === "") {
            return true;
          }

          return value.length >= 10 && value.length <= 11;
        })
        .test("client-phone-digits", "O número de telefone deve conter apenas números.", (value) => {
          if (value == null || value.trim() === "") {
            return true;
          }

          return /^\d+$/.test(value);
        }),
    otherwise: (schema) => schema.notRequired(),
  }),
  hasBiologicalSibling: yup.boolean().required("Campo obrigatório."),
  isTwinSibling: yup.boolean().when("hasBiologicalSibling", {
    is: (hasBiologicalSibling: boolean) => hasBiologicalSibling,
    then: (schema) => schema.required("Campo obrigatório."),
    otherwise: (schema) => schema.notRequired(),
  }),
  disability: yup.boolean().required("Campo é obrigatório."),
  disabilityType: yup
    .array()
    .of(yup.string().oneOf(Object.keys(DisabilityTypeEnum)))
    .when("hasDisability", {
      is: true,
      then: (schema) => schema.min(1, "Selecione pelo menos um tipo de deficiência."),
      otherwise: (schema) => schema.nullable(),
    }),
  genderIdentityDetails: yup.string().when("genderIdentity", {
    is: (genderIdentity: string) => genderIdentity === "OTHER",
    then: (schema) => schema.required("Campo obrigatório."),
    otherwise: (schema) => schema.notRequired(),
  }),
  address: yup.object().shape({
    zipCode: yup
      .string()
      .required("CEP é obrigatório.")
      .transform((value) => value.replace(/\D/g, ""))
      .matches(/^\d{8}$/, "CEP inválido."),
    street: yup.string().required("Rua é obrigatória."),
    number: yup.string().required("Número é obrigatório."),
    complement: yup.string().notRequired(),
    neighborhood: yup.string().required("Bairro é obrigatório."),
    city: yup.string().required("Cidade é obrigatória."),
    state: yup.string().required("Estado é obrigatório."),
  }),
  phonesUnderageOptional: yup.string().when("birthday", {
    is: (birthday: Date) => verifyIfTalentIsAdult(birthday),
    then: (schema) =>
      schema
        .required("Telefone é obrigatório.")
        .transform((value) => (value ? value.replace(/\D/g, "") : ""))
        .min(10, "O número de telefone deve ter pelo menos 10 dígitos.")
        .max(11, "O número de telefone deve ter no máximo 11 dígitos.")
        .matches(/^\d+$/, "O número de telefone deve conter apenas números."),
    otherwise: (schema) =>
      schema
        .transform((value) => (value ? value.replace(/\D/g, "") : ""))
        .notRequired()
        .test("phones-optional-min", "O número de telefone deve ter pelo menos 10 dígitos.", (value) => {
          if (value == null || value.trim() === "") {
            return true; // allow empty
          }
          return yup.string().min(10).isValidSync(value);
        })
        .test("phones-optional-max", "O número de telefone deve ter no máximo 10 dígitos.", (value) => {
          if (value == null || value.trim() === "") {
            return true; // allow empty
          }
          return yup.string().max(11).isValidSync(value);
        })
        .test("phones-optional-digits", "O número de telefone deve conter apenas números.", (value) => {
          if (value == null || value.trim() === "") {
            return true; // allow empty
          }
          return yup.string().matches(/^\d+$/).isValidSync(value);
        }),
  }),
  socialMedias: yup.array().of(
    yup.object().shape(
      {
        plataform: yup.string().when(["username", "followerRange"], {
          is: (username: string, followerRange: string) => username || followerRange,
          then: (schema) => schema.required("Rede social é obrigatória."),
          otherwise: (schema) => schema.notRequired(),
        }),
        username: yup.string().when(["plataform", "followerRange"], {
          is: (plataform: string, followerRange: string) => plataform || followerRange,
          then: (schema) => schema.required("Nome de usuário é obrigatório."),
          otherwise: (schema) => schema.notRequired(),
        }),
        followerRange: yup.string().when(["plataform", "username"], {
          is: (plataform: string, username: string) => plataform || username,
          then: (schema) => schema.required("Número de seguidores é obrigatório."),
          otherwise: (schema) => schema.notRequired(),
        }),
      },
      [
        ["plataform", "username"],
        ["plataform", "followerRange"],
        ["username", "followerRange"],
      ],
    ),
  ),
  characteristic: yup.object().shape({
    eyeColor: yup.string().notRequired(),
    naturalHairColor: yup.string().notRequired(),
    currentHairColor: yup.string().notRequired(),
    naturalHairType: yup.string().notRequired(),
    currentHairType: yup.string().notRequired(),
    skinColor: yup.string().notRequired(),
    physicalFeatures: yup
      .array()
      .of(yup.string().oneOf(Object.keys(PhysicalCharacteristicEnum)))
      .notRequired(),
    hasTattoo: yup.boolean().notRequired(),
  }),
  measurements: yup.object().shape({
    height: yup
      .string()
      .notRequired()
      .test("height-format", "Altura deve conter apenas números inteiros.", (value) => {
        if (!value || value.trim() === "") {
          return true;
        }
        return /^\d+$/.test(value);
      }),
    shoeSize: yup
      .array()
      .of(yup.string().oneOf(Object.keys(ShoeSizeEnum)))
      .notRequired(),
    clothingSize: yup
      .array()
      .of(yup.string().oneOf(Object.keys(ClothingSizeEnum)))
      .notRequired(),
  }),
  heightFilter: yup
    .object()
    .shape({
      from: yup
        .string()
        .test("is-valid-height-range", "Altura deve estar entre 40 e 250 cm.", validateHeightRange)
        .notRequired(),
      to: yup
        .string()
        .test("is-valid-height-range", "Altura deve estar entre 40 e 250 cm.", validateHeightRange)
        .notRequired(),
    })
    // TODO investigar por que essa mensagem não é exibida
    .test("is-valid-height-interval", "Insira um intervalo válido", function (value) {
      const { from, to } = value;
      // Se ambos estão vazios, é válido
      if (!from || from.trim() === "" || !to || to.trim() === "") {
        return true;
      }
      const fromNum = Number(from);
      const toNum = Number(to);
      // Se algum não é número, deixa a validação individual tratar
      if (Number.isNaN(fromNum) || Number.isNaN(toNum)) {
        return true;
      }
      // Validar se from <= to
      return fromNum <= toNum;
    }),
  responsibles: yup.array().when("birthday", {
    is: (birthday: Date) => verifyIfTalentIsAdult(birthday),
    then: () => yup.array().of(createResponsibleValidation(true)),
    otherwise: () => yup.array().of(createResponsibleValidation(false)).min(1, "Adicione ao menos um responsável."),
  }),
  abilities: yup.object().shape({
    languages: yup
      .array()
      .max(3, "Máximo de 3 idiomas permitidos.")
      .test("no-duplicates", "Idiomas duplicados não são permitidos.", function (languages) {
        if (!languages || languages.length === 0) {
          return true;
        }

        const languageNames = languages.map((language: any) => language?.name).filter(Boolean);
        const uniqueLanguageNames = new Set(languageNames);

        return languageNames.length === uniqueLanguageNames.size;
      })
      .of(
        yup
          .object()
          .shape({
            name: yup.string().notRequired(),
            proficiencyLevel: yup.string().notRequired(),
          })
          .test("language-pair-required", function (language) {
            const hasLanguageName = Boolean(language?.name);
            const hasProficiencyLevel = Boolean(language?.proficiencyLevel);

            if (hasLanguageName && !hasProficiencyLevel) {
              return this.createError({
                path: `${this.path}.proficiencyLevel`,
                message: "Nível do idioma é obrigatório quando o idioma é informado.",
              });
            }

            if (!hasLanguageName && hasProficiencyLevel) {
              return this.createError({
                path: `${this.path}.name`,
                message: "Idioma é obrigatório quando o nível é informado.",
              });
            }

            return true;
          }),
      ),
    othersSports: yup.string().when("sports", {
      is: (sports: string[]) => sports?.includes("OTHER"),
      then: (schema) =>
        schema.required("Campo obrigatório quando 'Outro' é selecionado.").max(100, "Limite de 100 caracteres."),
      otherwise: (schema) => schema.notRequired(),
    }),
    othersMusic: yup.string().when("music", {
      is: (music: string[]) => music?.includes("OTHER"),
      then: (schema) =>
        schema.required("Campo obrigatório quando 'Outro' é selecionado.").max(100, "Limite de 100 caracteres."),
      otherwise: (schema) => schema.notRequired(),
    }),
    othersDance: yup.string().when("dance", {
      is: (dance: string[]) => dance?.includes("OTHER"),
      then: (schema) =>
        schema.required("Campo obrigatório quando 'Outro' é selecionado.").max(100, "Limite de 100 caracteres."),
      otherwise: (schema) => schema.notRequired(),
    }),
    sports: yup.array().of(yup.string()).notRequired(),
    music: yup.array().of(yup.string()).notRequired(),
    dance: yup.array().of(yup.string()).notRequired(),
  }),
  email: yup.string().required("Email é obrigatório.").email("Digite um e-mail válido."),
  emailOptional: yup
    .string()
    .notRequired()
    .test(
      "email-optional",
      "Digite um e-mail válido.",
      (value) => !value?.trim() || yup.string().email().isValidSync(value),
    ),
  phoneOptional: yup
    .string()
    .transform((value) => (value ? value.replace(/\D/g, "") : ""))
    .notRequired()
    .test("phone-optional", "O número de telefone deve ter entre 10 e 11 dígitos.", (value) => {
      if (!value?.trim()) {
        return true;
      }

      return value.length >= 10 && value.length <= 11;
    }),
  scheduleTalentCode: yup.string().when("scheduleType", {
    is: ScheduleType.PHOTO_RENEWAL,
    then: (schema) => schema.required(requiredMessage),
    otherwise: (schema) => schema.notRequired(),
  }),
  photoMaterialType: yup.string().when("scheduleType", {
    is: ScheduleType.PHOTO_RENEWAL,
    then: (schema) => schema.required(requiredMessage),
    otherwise: (schema) => schema.notRequired(),
  }),
  newCandidateOrigin: yup.string().when("scheduleType", {
    is: ScheduleType.NEW_CANDIDATE,
    then: (schema) => schema.required(requiredMessage),
    otherwise: (schema) => schema.notRequired(),
  }),
  newCandidateFullName: yup.string().when("scheduleType", {
    is: ScheduleType.NEW_CANDIDATE,
    then: (schema) => schema.required(requiredMessage).min(3, "Digite um nome válido."),
    otherwise: (schema) => schema.notRequired(),
  }),
  newCandidateBirthday: yup
    .date()
    .nullable()
    .when("scheduleType", {
      is: ScheduleType.NEW_CANDIDATE,
      then: (schema) => schema.required("Data de nascimento é obrigatória."),
      otherwise: (schema) => schema.notRequired(),
    }),
  newCandidateResponsibleFullName: yup.string().when("candidateBirthday", {
    is: (birthday: Date | null) => birthday instanceof Date && !verifyIfTalentIsAdult(birthday),
    then: (schema) => schema.required(requiredMessage).min(3, "Digite um nome válido."),
    otherwise: (schema) => schema.notRequired(),
  }),
  newCandidateResponsibleEmail: yup.string().when("candidateBirthday", {
    is: (birthday: Date | null) => birthday instanceof Date && !verifyIfTalentIsAdult(birthday),
    then: (schema) => schema.required(requiredMessage).email("Digite um e-mail válido."),
    otherwise: (schema) => schema.notRequired(),
  }),
  newCandidateResponsiblePhone: yup.string().when("candidateBirthday", {
    is: (birthday: Date | null) => birthday instanceof Date && !verifyIfTalentIsAdult(birthday),
    then: (schema) =>
      schema
        .required(requiredMessage)
        .transform((value) => (value ? value.replace(/\D/g, "") : ""))
        .length(11, "O número de telefone deve ter 11 dígitos."),
    otherwise: (schema) => schema.notRequired(),
  }),
  emailUnderageOptional: yup.string().when("birthday", {
    is: (birthday: Date) => verifyIfTalentIsAdult(birthday),
    then: (schema) => schema.required("Email é obrigatório.").email("Digite um e-mail válido"),
    otherwise: (schema) =>
      schema.notRequired().test("email-optional", "Digite um e-mail válido.", (value) => {
        if (value == null || value.trim() === "") {
          return true; // allow empty
        }
        return yup.string().email().isValidSync(value);
      }),
  }),
  password: yup
    .string()
    .required("Senha é obrigatória.")
    .matches(
      /^(?=.*[A-Za-z])(?=.*\d)(?=.{8,})/,
      "A senha deve ter pelo menos 8 caracteres, contendo letras e números.",
    ),
  weakPassword: yup.string().required("Senha é obrigatória."),
  passwordConfirmation: yup
    .string()
    .nullable()
    .oneOf([yup.ref("password"), null], "Deve ser igual à senha."),
  required: yup.lazy((value) => {
    const input = Array.isArray(value) ? value[0] : value;
    const type = typeof input;
    const schema: yup.Schema = type === "number" ? yup.number() : yup.string();

    let base: yup.Schema;

    if (Array.isArray(value)) {
      base = yup.array().of(schema).min(1, "Selecione ao menos uma opção.");
    } else {
      base = schema;
    }

    return base.nullable().required(requiredMessage);
  }),
  title: yup.string().required("Título é obrigatório.").min(3, "Digite um título válido."),
  description: yup.string().required("Descrição é obrigatória.").min(3, "Digite uma descrição válida."),
  html: yup
    .string()
    .required("Texto é obrigatório.")
    .test("is-html", "Digite um texto válido.", (value) => {
      return getHtmlContent(value).trim().length >= 3;
    }),
  currencyString: yup.string().test("is-currency", "Digite um valor válido.", (value) => {
    if (!value) {
      return true;
    }
    const amount = Number(value.replace(/\D/g, ""));
    return amount > 0 && amount <= 9999999.99;
  }),
  dateInterval: yup
    .array()
    .of(yup.date())
    .required("Período é obrigatório.")
    .test("is-date-interval", "Período inválido.", (dates) => {
      if (dates?.filter(Boolean).length !== 2) {
        return false;
      }

      const [start, end] = dates as [Date, Date];

      return start < end;
    }),
  dateIntervalOptional: yup
    .array()
    .of(yup.date().nullable())
    .nullable()
    .notRequired()
    .test("is-date-interval", "Período inválido.", (dates) => {
      const validDates = dates?.filter(Boolean) ?? [];

      if (validDates.length === 0) {
        return true;
      }

      if (validDates.length !== 2) {
        return false;
      }

      const [startDate, endDate] = validDates as [Date, Date];

      return startDate < endDate;
    }),
  currency: yup
    .number()
    .required("Valor é obrigatório.")
    .min(1, "Valor deve ser maior que zero.")
    .max(9999999.99, "Valor acima do limite permitido."),
  role: yup.string().required("Permissão é obrigatória."),
  department: yup.string().required("Departamento é obrigatório."),
  ageFilter: yup
    .object()
    .shape({
      years: yup
        .object()
        .shape({
          from: createOptionalAgeRangeValidation(),
          to: createOptionalAgeRangeValidation(),
        })
        .notRequired()
        .test("age-years-order", "Idade mínima não pode ser maior que a máxima.", (value) => {
          return isAgeRangeValid(value);
        }),
      birthYear: yup
        .object()
        .shape({
          from: createOptionalNumericRangeValidation(1900, CURRENT_YEAR, "Ano de nascimento"),
          to: createOptionalNumericRangeValidation(1900, CURRENT_YEAR, "Ano de nascimento"),
        })
        .notRequired()
        // TODO verificar por quê essa validação não está funcionando
        .test("birthYear-order", "Ano de nascimento mínimo não pode ser maior que o máximo.", (value) => {
          return isNumericRangeValid(value);
        }),
      months: yup
        .object()
        .shape({
          from: createOptionalNumericRangeValidation(1, 11, "Mês"),
          to: createOptionalNumericRangeValidation(1, 11, "Mês"),
        })
        .notRequired()
        // TODO verificar por quê essa validação não está funcionando
        .test("months-order", "Mês mínimo não pode ser maior que o máximo.", (value) => {
          return isNumericRangeValid(value);
        }),
    })
    .notRequired(),
  fullDate: yup.object().shape({
    initialDate: createFullDateValidation(),
    finalDate: createFullDateValidation(),
  }),
};
